import * as utils from './utils';

export function processStatusEffects(data) {
  if (data.player.status !== 'normal' && data.player.status_duration > 0) {
    data.player.status_duration--;
    
    if (data.player.status === 'poisoned') {
      const damage = 3;
      data.player.hp = Math.max(0, data.player.hp - damage);
      utils.updateTypedBox(data.infoBox, [utils.prefix(`You take ${damage} poison damage! HP: ${data.player.hp}/${data.player.max_hp}`)]);
      if (data.player.hp === 0) {
        utils.updateTypedBox(data.infoBox, [utils.prefix("You died from poison... Game Over. Type 'load' to continue or refresh to restart.")]);
        data.gameOver = true;
      }
    }
    
    if (data.player.status_duration === 0) {
      data.player.status = 'normal';
      utils.updateTypedBox(data.infoBox, [utils.prefix("Status effect wore off.")]);
    }
  }
}

export function changeChapter(data) {
  if (data.actualRoom.end_room && data.command.toLowerCase().includes('next')) {
    const nextChapter = data.chapterNum + 1;
    const chapter = utils.getChapter(nextChapter);
    
    if (!chapter) {
      utils.updateTypedBox(data.infoBox, [utils.prefix("Congratulations! You completed the game!")]);
      data.command = '';
      return true;
    }
    
    data.chapterNum = nextChapter;
    data.chapter = chapter;
    data.actualRoom = utils.firstRoom(chapter);
    data.moveHistory = [];
    
    // Auto-save when changing chapter
    const state = {
      chapterNum: data.chapterNum,
      roomId: data.actualRoom.id,
      inventory: data.inventory,
      player: data.player,
      moveHistory: data.moveHistory
    };
    utils.saveGame(state);
    
    utils.updateTypedBox(data.titleBox, [utils.prefix(`CHAPTER ${nextChapter}: ${chapter.name}<br/>${data.actualRoom.name}`)]);
    utils.updateTypedBox(data.descBox, [utils.prefix(chapter.story + "<br/><br/>" + data.actualRoom.description)]);
    utils.updateTypedBox(data.errorBox, ['']);
    utils.updateTypedBox(data.infoBox, [utils.prefix("Progress auto-saved!")], 0, 5);
    
    data.command = '';
    data.actionAllowed = true;
    return true;
  }
  return false;
}

export function moveCommands(data) {
  const commands = utils.moveCommands();
  
  for (const dir of commands) {
    if (data.command.toLowerCase().includes(dir)) {
      const moves = data.actualRoom.moves || {};
      
      if (moves[dir]) {
        const locks = data.actualRoom.locks || {};
        
        if (locks[dir]) {
          const lock = locks[dir];
          const hasItems = lock.required_items.every(item => data.inventory.includes(item));
          
          if (!hasItems) {
            utils.updateTypedBox(data.errorBox, [utils.prefix(lock.error)]);
            data.command = '';
            data.actionAllowed = true;
            return true;
          }
          
          // Consume items if needed
          lock.required_items.forEach(item => {
            const itemData = utils.getItemData(data.chapter, item);
            if (itemData?.consumable) {
              data.inventory = data.inventory.filter(i => i !== item);
            }
          });
        }
        
        const nextRoomId = moves[dir];
        
        if (nextRoomId.startsWith('chapter_')) {
          if (data.actualRoom.end_room) {
            utils.updateTypedBox(data.infoBox, [utils.prefix("Chapter complete! Type 'next' to continue.")]);
            data.command = '';
            data.actionAllowed = true;
            return true;
          }
        }
        
        data.actualRoom = utils.getRoom(data.chapter, nextRoomId);
        data.moveHistory.push({ direction: dir, room: data.actualRoom.name });
        
        utils.updateTypedBox(data.titleBox, [utils.prefix(`CHAPTER ${data.chapterNum}: ${data.chapter.name}<br/>${data.actualRoom.name}`)]);
        utils.updateTypedBox(data.descBox, [utils.prefix(data.actualRoom.description)]);
        utils.updateTypedBox(data.errorBox, ['']);
        
        // Check for enemies
        if (data.actualRoom.enemies && data.actualRoom.enemies.length > 0) {
          data.inCombat = true;
          data.currentEnemy = { ...data.actualRoom.enemies[0] };
          utils.updateTypedBox(data.infoBox, [utils.prefix(`A ${data.currentEnemy.name} appears! HP: ${data.currentEnemy.hp}<br/>Commands: attack [weapon], defend, flee`)]);
        }
        
        data.command = '';
        data.actionAllowed = true;
        return true;
      }
    }
  }
  return false;
}

export function combatCommands(data) {
  if (!data.inCombat) return false;
  
  const cmd = data.command.toLowerCase();
  
  // Attack
  if (cmd.includes('attack')) {
    const weapons = data.inventory.filter(i => utils.getItemData(data.chapter, i)?.type === 'weapon');
    
    let damage = 5; // Base unarmed damage
    let weaponName = "bare hands";
    
    // Check if a specific weapon was mentioned
    for (const w of weapons) {
      if (cmd.includes(w.replace('_', ' '))) {
        const weaponData = utils.getItemData(data.chapter, w);
        damage = weaponData.damage;
        weaponName = weaponData.name;
        break;
      }
    }
    
    data.currentEnemy.hp -= damage;
    
    let msg = `You attack with ${weaponName} for ${damage} damage!<br/>`;
    
    if (data.currentEnemy.hp <= 0) {
      msg += `${data.currentEnemy.name} defeated!`;
      data.inCombat = false;
      data.currentEnemy = null;
      data.actualRoom.enemies = [];
      utils.updateTypedBox(data.infoBox, [utils.prefix(msg)]);
      data.command = '';
      data.actionAllowed = true;
      return true;
    }
    
    // Enemy attacks back
    const enemyDamage = data.currentEnemy.damage;
    data.player.hp = Math.max(0, data.player.hp - enemyDamage);
    msg += `${data.currentEnemy.name} attacks for ${enemyDamage} damage!<br/>`;
    msg += `Enemy HP: ${data.currentEnemy.hp} | Your HP: ${data.player.hp}/${data.player.max_hp}`;
    
    // Check status effects
    if (data.currentEnemy.status_effects) {
      for (const [effect, data_effect] of Object.entries(data.currentEnemy.status_effects)) {
        if (Math.random() < data_effect.chance) {
          data.player.status = effect;
          data.player.status_duration = data_effect.duration;
          msg += `<br/>You are ${effect}!`;
        }
      }
    }
    
    if (data.player.hp === 0) {
      msg += "<br/>You died... Type 'load' or refresh.";
      data.gameOver = true;
    }
    
    utils.updateTypedBox(data.infoBox, [utils.prefix(msg)]);
    data.command = '';
    data.actionAllowed = true;
    return true;
  }
  
  // Defend
  if (cmd.includes('defend')) {
    const enemyDamage = Math.floor(data.currentEnemy.damage / 2);
    data.player.hp = Math.max(0, data.player.hp - enemyDamage);
    
    let msg = `You defend! ${data.currentEnemy.name} attacks for ${enemyDamage} damage (reduced).<br/>`;
    msg += `Enemy HP: ${data.currentEnemy.hp} | Your HP: ${data.player.hp}/${data.player.max_hp}`;
    
    if (data.player.hp === 0) {
      msg += "<br/>You died... Type 'load' or refresh.";
      data.gameOver = true;
    }
    
    utils.updateTypedBox(data.infoBox, [utils.prefix(msg)]);
    data.command = '';
    data.actionAllowed = true;
    return true;
  }
  
  // Flee
  if (cmd.includes('flee')) {
    const fleeChance = data.currentEnemy.flee_chance || 0.5;
    
    if (Math.random() < fleeChance) {
      utils.updateTypedBox(data.infoBox, [utils.prefix("You fled successfully!")]);
      data.inCombat = false;
      data.currentEnemy = null;
      
      // Move back if possible
      if (data.moveHistory.length > 0) {
        const lastMove = data.moveHistory[data.moveHistory.length - 1];
        const opposites = { north: 'south', south: 'north', east: 'west', west: 'east', up: 'down', down: 'up' };
        const backDir = opposites[lastMove.direction];
        
        if (backDir && data.actualRoom.moves[backDir]) {
          data.actualRoom = utils.getRoom(data.chapter, data.actualRoom.moves[backDir]);
          utils.updateTypedBox(data.descBox, [utils.prefix(data.actualRoom.description)]);
        }
      }
    } else {
      const enemyDamage = data.currentEnemy.damage;
      data.player.hp = Math.max(0, data.player.hp - enemyDamage);
      
      let msg = `Failed to flee! ${data.currentEnemy.name} attacks for ${enemyDamage} damage!<br/>`;
      msg += `Your HP: ${data.player.hp}/${data.player.max_hp}`;
      
      if (data.player.hp === 0) {
        msg += "<br/>You died... Type 'load' or refresh.";
        data.gameOver = true;
      }
      
      utils.updateTypedBox(data.infoBox, [utils.prefix(msg)]);
    }
    
    data.command = '';
    data.actionAllowed = true;
    return true;
  }
  
  return false;
}

export function lookupCommands(data) {
  const commands = utils.lookupCommands();
  
  for (const action of commands) {
    if (data.command.toLowerCase().includes(action)) {
      utils.updateTypedBox(data.errorBox, ['']);
      
      // Check for searchable objects
      const searchable = data.actualRoom.searchable || {};
      const searchTerm = data.command.toLowerCase().replace(action, '').trim();
      
      if (searchTerm && searchable[searchTerm]) {
        const obj = searchable[searchTerm];
        data.lastSearched = searchTerm;
        data.lastItemSeen = null; // Reset lastItemSeen when searching
        utils.updateTypedBox(data.infoBox, [utils.prefix(`${obj.description}<br/>Items: ${obj.items.join(', ')}`)]);
        data.command = '';
        data.actionAllowed = true;
        return true;
      }
      
      // Check for items in room
      if (data.actualRoom.items && data.actualRoom.items.length > 0) {
        data.lastItemSeen = data.actualRoom.items[0];
        data.lastSearched = null; // Reset lastSearched when looking
        utils.updateTypedBox(data.infoBox, [utils.prefix(utils.lookupResultMsg(data.lastItemSeen))]);
      } else {
        utils.updateTypedBox(data.infoBox, [utils.prefix("Nothing interesting here.")]);
        data.lastSearched = null; // Reset both
        data.lastItemSeen = null;
      }
      
      data.command = '';
      data.actionAllowed = true;
      return true;
    }
  }
  return false;
}

export function getCommands(data) {
  const commands = utils.getCommands();
  
  for (const action of commands) {
    if (data.command.toLowerCase().includes(action)) {
      utils.updateTypedBox(data.errorBox, ['']);
      
      const itemName = data.command.toLowerCase().replace(action, '').trim().replace(/ /g, '_');
      
      // Try to get specific item from searchable first
      if (itemName) {
        const searchable = data.actualRoom.searchable || {};
        for (const obj of Object.values(searchable)) {
          if (obj.items.includes(itemName)) {
            data.inventory.push(itemName);
            obj.items = obj.items.filter(i => i !== itemName);
            utils.updateTypedBox(data.infoBox, [utils.prefix(utils.itemGotMsg(itemName))]);
            data.lastSearched = null;
            data.command = '';
            data.actionAllowed = true;
            return true;
          }
        }
        
        // Try to get specific item from room
        if (data.actualRoom.items && data.actualRoom.items.includes(itemName)) {
          data.inventory.push(itemName);
          data.actualRoom.items = data.actualRoom.items.filter(i => i !== itemName);
          utils.updateTypedBox(data.infoBox, [utils.prefix(utils.itemGotMsg(itemName))]);
          data.lastItemSeen = null;
          data.command = '';
          data.actionAllowed = true;
          return true;
        }
      }
      
      // Get from searchable (after search command)
      if (data.lastSearched) {
        const searchable = data.actualRoom.searchable || {};
        const obj = searchable[data.lastSearched];
        
        if (obj && obj.items.length > 0) {
          const item = obj.items[0];
          data.inventory.push(item);
          obj.items.shift();
          utils.updateTypedBox(data.infoBox, [utils.prefix(utils.itemGotMsg(item))]);
          data.lastSearched = obj.items.length > 0 ? data.lastSearched : null;
          data.command = '';
          data.actionAllowed = true;
          return true;
        }
      }
      
      // Get from room (after look command)
      if (data.lastItemSeen) {
        data.inventory.push(data.lastItemSeen);
        data.actualRoom.items = data.actualRoom.items.filter(i => i !== data.lastItemSeen);
        utils.updateTypedBox(data.infoBox, [utils.prefix(utils.itemGotMsg(data.lastItemSeen))]);
        data.lastItemSeen = null;
        data.command = '';
        data.actionAllowed = true;
        return true;
      }
      
      utils.updateTypedBox(data.errorBox, [utils.prefix("Nothing to take.")]);
      data.command = '';
      data.actionAllowed = true;
      return true;
    }
  }
  return false;
}

export function useCommands(data) {
  const commands = utils.useCommands();
  
  for (const action of commands) {
    if (data.command.toLowerCase().includes(action)) {
      const itemName = data.command.toLowerCase().replace(action, '').trim().replace(/ /g, '_');
      
      if (!data.inventory.includes(itemName)) {
        utils.updateTypedBox(data.errorBox, [utils.prefix("You don't have that item.")]);
        data.command = '';
        data.actionAllowed = true;
        return true;
      }
      
      const itemData = utils.getItemData(data.chapter, itemName);
      
      if (itemData.type === 'healing') {
        data.player.hp = Math.min(data.player.max_hp, data.player.hp + itemData.heal);
        if (itemData.consumable) {
          data.inventory = data.inventory.filter(i => i !== itemName);
        }
        utils.updateTypedBox(data.infoBox, [utils.prefix(`Used ${itemData.name}. HP: ${data.player.hp}/${data.player.max_hp}`)]);
        data.command = '';
        data.actionAllowed = true;
        return true;
      }
      
      if (itemData.type === 'cure') {
        if (data.player.status === itemData.cures) {
          data.player.status = 'normal';
          data.player.status_duration = 0;
          if (itemData.consumable) {
            data.inventory = data.inventory.filter(i => i !== itemName);
          }
          utils.updateTypedBox(data.infoBox, [utils.prefix(`Cured ${itemData.cures}!`)]);
        } else {
          utils.updateTypedBox(data.infoBox, [utils.prefix("You don't need this right now.")]);
        }
        data.command = '';
        data.actionAllowed = true;
        return true;
      }
      
      utils.updateTypedBox(data.errorBox, [utils.prefix("Can't use that here.")]);
      data.command = '';
      data.actionAllowed = true;
      return true;
    }
  }
  return false;
}

export function inventoryCommands(data) {
  const commands = utils.inventoryCommands();
  
  for (const action of commands) {
    if (data.command.toLowerCase().includes(action)) {
      utils.updateTypedBox(data.errorBox, ['']);
      utils.updateTypedBox(data.infoBox, [utils.prefix(utils.inventoryMsg(data.chapter, data.inventory, data.player))]);
      data.command = '';
      data.actionAllowed = true;
      return true;
    }
  }
  return false;
}

export function compassCommands(data) {
  const commands = utils.compassCommands();
  
  for (const action of commands) {
    if (data.command.toLowerCase().includes(action)) {
      utils.updateTypedBox(data.infoBox, [utils.prefix(utils.compassMsg(data.moveHistory))]);
      data.command = '';
      data.actionAllowed = true;
      return true;
    }
  }
  return false;
}

export function mapCommands(data) {
  const commands = utils.mapCommands();
  
  for (const action of commands) {
    if (data.command.toLowerCase().includes(action)) {
      utils.updateTypedBox(data.infoBox, [utils.prefix(utils.generateMap(data.actualRoom, data.chapter))], 0, 0);
      data.command = '';
      data.actionAllowed = true;
      return true;
    }
  }
  return false;
}

export function saveCommands(data) {
  const commands = utils.saveCommands();
  
  for (const action of commands) {
    if (data.command.toLowerCase().includes(action)) {
      const state = {
        chapterNum: data.chapterNum,
        roomId: data.actualRoom.id,
        inventory: data.inventory,
        player: data.player,
        moveHistory: data.moveHistory
      };
      
      if (utils.saveGame(state)) {
        utils.updateTypedBox(data.infoBox, [utils.prefix("Game saved!")]);
      } else {
        utils.updateTypedBox(data.errorBox, [utils.prefix("Save failed.")]);
      }
      
      data.command = '';
      data.actionAllowed = true;
      return true;
    }
  }
  return false;
}

export function loadCommands(data) {
  const commands = utils.loadCommands();
  
  for (const action of commands) {
    if (data.command.toLowerCase().includes(action)) {
      const state = utils.loadGame();
      
      if (state) {
        data.chapterNum = state.chapterNum;
        data.chapter = utils.getChapter(state.chapterNum);
        data.actualRoom = utils.getRoom(data.chapter, state.roomId);
        data.inventory = state.inventory;
        data.player = state.player;
        data.moveHistory = state.moveHistory || [];
        data.gameOver = false;
        data.inCombat = false;
        
        utils.updateTypedBox(data.titleBox, [utils.prefix(`CHAPTER ${data.chapterNum}: ${data.chapter.name}<br/>${data.actualRoom.name}`)]);
        utils.updateTypedBox(data.descBox, [utils.prefix(data.actualRoom.description)]);
        utils.updateTypedBox(data.infoBox, [utils.prefix("Game loaded!")]);
      } else {
        utils.updateTypedBox(data.errorBox, [utils.prefix("No save found.")]);
      }
      
      data.command = '';
      data.actionAllowed = true;
      return true;
    }
  }
  return false;
}

export function helpCommands(data) {
  const commands = utils.helpCommands();
  
  for (const action of commands) {
    if (data.command.toLowerCase().includes(action)) {
      utils.updateTypedBox(data.errorBox, ['']);
      utils.updateTypedBox(data.infoBox, [utils.prefix(utils.helpMsg())], 0, 10);
      data.command = '';
      data.actionAllowed = true;
      return true;
    }
  }
  return false;
}
